import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { ErrorSummary } from '@loctopus/ui';
import { api } from '../../lib/api';
import { toParsedError } from '../../lib/errorDisplay';
import { formatDateTime, formatMinor } from '../../lib/format';
import { BarChart, LineChart } from '../../components/charts';
import { Card, DataTable, FormField, PageHeader, Select, Spinner } from '../../components/ui';
import type { Order } from '../../lib/types';

export function ReportsPage() {
  const navigate = useNavigate();
  const [granularity, setGranularity] = useState<'day' | 'week' | 'month'>('day');

  const reportQuery = useQuery({
    queryKey: ['sales-report', granularity],
    queryFn: () => api.reports.sales({ granularity }),
  });

  // Owner's explicit ask: "when (time and date) and tickets were sold, how much the buyer paid,
  // what promo code was used, how many tickets bought... show general details in reports
  // dashboard, and when clicked on that specific transaction it should show all details" - this
  // table IS that list; row click navigates to the existing, now-promo-code-aware order detail
  // page (features/orders/OrderDetailPage.tsx) for "every detail possible".
  const transactionsQuery = useQuery({
    queryKey: ['reports-transactions'],
    queryFn: () => api.orders.list({ limit: 100 }),
  });
  // REAL BUG FOUND AND FIXED: this showed every order regardless of status, including
  // `pending` ones that were started and abandoned (never paid) and `cancelled`/`disputed` ones -
  // none of those ever actually issued a ticket to anyone. Owner's explicit correction: this
  // table should reflect real transactions - orders a ticket's QR code was genuinely sent for -
  // not every checkout attempt that was ever started. `paid` is included alongside `fulfilled`
  // since ticket issuance happens in the same step as that status flip (queue/consumer.ts) -
  // by the time an order is visibly `paid` here its tickets already exist.
  const ISSUED_TICKET_STATUSES = new Set(['paid', 'fulfilled', 'partially_refunded', 'refunded']);
  const transactions = (transactionsQuery.data?.data ?? []).filter((o) =>
    ISSUED_TICKET_STATUSES.has(o.status),
  );

  const report = reportQuery.data;
  const grossPoints =
    report?.points.map((p) => ({ label: p.bucket, value: p.grossMinor / 100 })) ?? [];
  const ticketPoints = report?.points.map((p) => ({ label: p.bucket, value: p.ticketsSold })) ?? [];

  return (
    <div>
      <PageHeader
        title="Sales reports"
        description="Gross/net revenue and tickets sold over time."
      />
      <Card className="mb-6">
        <FormField label="Granularity">
          <Select
            value={granularity}
            onChange={(e) => setGranularity(e.target.value as 'day' | 'week' | 'month')}
          >
            <option value="day">Daily</option>
            <option value="week">Weekly</option>
            <option value="month">Monthly</option>
          </Select>
        </FormField>
      </Card>
      {reportQuery.isLoading ? (
        <Spinner />
      ) : reportQuery.error ? (
        <ErrorSummary error={toParsedError(reportQuery.error)} />
      ) : report ? (
        <>
          <div className="grid grid-cols-4 gap-4 mb-6">
            <Card title="Gross">{formatMinor(report.totals.grossMinor, report.currency)}</Card>
            <Card title="Net">{formatMinor(report.totals.netMinor, report.currency)}</Card>
            <Card title="Tickets sold">{report.totals.ticketsSold}</Card>
            <Card title="Orders">{report.totals.ordersCount}</Card>
          </div>
          <Card title="Gross revenue" className="mb-6">
            <BarChart points={grossPoints} />
          </Card>
          <Card title="Tickets sold">
            <LineChart points={ticketPoints} />
          </Card>
        </>
      ) : null}

      <Card title="Transactions" className="mt-6">
        {transactionsQuery.isLoading ? (
          <Spinner />
        ) : transactionsQuery.error ? (
          <ErrorSummary error={toParsedError(transactionsQuery.error)} />
        ) : (
          <DataTable<Order>
            columns={[
              {
                key: 'when',
                header: 'Date & time',
                render: (o) => formatDateTime(o.createdAt),
              },
              { key: 'buyer', header: 'Buyer', render: (o) => o.buyerEmail },
              {
                key: 'tickets',
                header: 'Tickets',
                render: (o) => o.ticketCount ?? '-',
              },
              {
                key: 'promo',
                header: 'Promo code',
                render: (o) => o.promoCode ?? '-',
              },
              {
                key: 'paid',
                header: 'Amount paid',
                render: (o) => formatMinor(o.totalMinor, o.currency),
              },
            ]}
            rows={transactions}
            getRowKey={(o) => o.id}
            onRowClick={(o) => navigate(`/orders/${o.id}`)}
            emptyMessage="No transactions yet."
          />
        )}
      </Card>
    </div>
  );
}
